import { useQueries } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { type SelectDataSource } from './SelectDataSource';

const HYDRATION_BATCH_SIZE = 5;

function isForbiddenError(error: unknown) {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'status' in error.response &&
    error.response.status === 403
  );
}

function isNotFoundError(error: unknown) {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof error.response === 'object' &&
    error.response !== null &&
    'status' in error.response &&
    error.response.status === 404
  );
}

function retryHydration(failureCount: number, error: unknown) {
  return (
    !isForbiddenError(error) && !isNotFoundError(error) && failureCount < 2
  );
}

function getHydrationRetryDelay(attempt: number) {
  return Math.min(1000 * 2 ** attempt, 10_000);
}

type UseAsyncSelectHydrationOptions<TItem, TPageParam, TFilters> = {
  readonly dataSource: SelectDataSource<TItem, TPageParam, TFilters>;
  readonly emptyValue?: string;
  readonly getOptionValue: (item: TItem) => string;
  readonly selectedValues: ReadonlySet<string>;
  readonly values: readonly string[];
};

export function useAsyncSelectHydration<TItem, TPageParam, TFilters>({
  dataSource,
  emptyValue,
  getOptionValue,
  selectedValues,
  values,
}: UseAsyncSelectHydrationOptions<TItem, TPageParam, TFilters>) {
  const cachedItems = values.flatMap((selectedValue) => {
    const cachedItem = dataSource.findCachedItem?.(selectedValue);
    return cachedItem ? [cachedItem] : [];
  });
  const cachedValues = new Set(cachedItems.map(getOptionValue));
  const itemQueryEntries = values.flatMap((selectedValue) => {
    if (
      selectedValues.has(selectedValue) ||
      cachedValues.has(selectedValue) ||
      selectedValue === emptyValue
    ) {
      return [];
    }

    const options = dataSource.itemQueryOptions?.(selectedValue);
    return options ? [{ options, value: selectedValue }] : [];
  });
  const hydrationKey = itemQueryEntries
    .map(({ value }) => value)
    .join('\u0000');
  const [hydrationBatch, setHydrationBatch] = useState({
    key: hydrationKey,
    size: HYDRATION_BATCH_SIZE,
  });
  if (hydrationBatch.key !== hydrationKey) {
    setHydrationBatch({ key: hydrationKey, size: HYDRATION_BATCH_SIZE });
  }
  const hydrationBatchSize =
    hydrationBatch.key === hydrationKey
      ? hydrationBatch.size
      : HYDRATION_BATCH_SIZE;
  const activeEntries = itemQueryEntries.slice(0, hydrationBatchSize);
  const itemQueries = useQueries({
    queries: activeEntries.map(({ options }) => ({
      ...options,
      retry: options.retry ?? retryHydration,
      retryDelay: options.retryDelay ?? getHydrationRetryDelay,
      retryOnMount: options.retryOnMount ?? false,
      throwOnError: options.throwOnError ?? false,
    })),
  });
  const hasFinishedBatch =
    itemQueries.length === activeEntries.length &&
    itemQueries.every(({ isError, isSuccess }) => isError || isSuccess);

  useEffect(() => {
    if (hasFinishedBatch && activeEntries.length < itemQueryEntries.length) {
      // A settled batch releases the next bounded group of detail requests.
      // oxlint-disable-next-line react/set-state-in-effect, react-hooks-extra/no-direct-set-state-in-use-effect
      setHydrationBatch((current) => ({
        ...current,
        size: current.size + HYDRATION_BATCH_SIZE,
      }));
    }
  }, [activeEntries.length, hasFinishedBatch, itemQueryEntries.length]);

  return {
    // A value whose item query has failed at least once, including one that
    // is still retrying.
    failedValues: new Set(
      activeEntries
        .filter((_, index) => itemQueries[index]?.failureCount)
        .map(({ value }) => value),
    ),
    resolvedItems: [
      ...cachedItems,
      ...itemQueries.flatMap(({ data }) =>
        data === undefined ? [] : [data as TItem],
      ),
    ],
    resolvableValues: new Set(itemQueryEntries.map(({ value }) => value)),
  };
}
